import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Send, ExternalLink } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { projectAPI } from '@/utils/supabase/client';
import type { AdminTask, InternProfile, TaskNote, InternLink } from '../types';
import type { TaskStatus } from '@features/intern/types';
import { STATUS_LABEL, STATUS_BADGE, TASK_STATUSES, dueLabel } from '../helpers';

const UNASSIGNED = '__unassigned__';

interface Props {
  task: AdminTask | null;
  interns: InternProfile[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChanged: () => void;
}

const authorLabel: Record<TaskNote['author'], string> = {
  worker: 'Intern',
  claude: 'Claude',
  ken: 'Admin',
};

export function TaskDetailSheet({ task, interns, open, onOpenChange, onChanged }: Props) {
  const [notes, setNotes] = useState<TaskNote[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [reply, setReply] = useState('');
  const [posting, setPosting] = useState(false);
  const [savingField, setSavingField] = useState(false);

  useEffect(() => {
    if (!task || !open) return;
    setReply('');
    setLoadingNotes(true);
    projectAPI
      .getInternTaskNotes(task.id)
      .then(setNotes)
      .catch((err) => toast.error(err instanceof Error ? err.message : 'Failed to load notes'))
      .finally(() => setLoadingNotes(false));
  }, [task?.id, open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!task) return null;

  async function patch(updates: Parameters<typeof projectAPI.updateInternTask>[1]) {
    if (!task) return;
    setSavingField(true);
    try {
      await projectAPI.updateInternTask(task.id, updates);
      toast.success('Task updated');
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update task');
    } finally {
      setSavingField(false);
    }
  }

  async function postReply() {
    if (!task || !reply.trim()) return;
    setPosting(true);
    try {
      const note = await projectAPI.addInternTaskNote(task.id, reply.trim());
      setNotes((prev) => [...prev, note]);
      setReply('');
      toast.success('Feedback sent');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send feedback');
    } finally {
      setPosting(false);
    }
  }

  const assigneeValue = task.assignee_id ?? UNASSIGNED;
  const links = (task.links || []) as InternLink[];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center gap-2">
            <Badge variant={STATUS_BADGE[task.status]}>{STATUS_LABEL[task.status]}</Badge>
            {task.is_critical && <Badge variant="destructive">Critical</Badge>}
          </div>
          <SheetTitle className="text-left">{task.title}</SheetTitle>
          <SheetDescription className="text-left">
            {dueLabel(task)}
            {task.assignee_name ? ` · ${task.assignee_name}` : ' · Unassigned'}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 px-4 pb-6">
          {task.description && (
            <p className="text-sm text-foreground/80 whitespace-pre-wrap leading-relaxed">
              {task.description}
            </p>
          )}

          {links.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {links.map((l, idx) => (
                <a
                  key={idx}
                  href={l.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  <ExternalLink className="size-3" />
                  {l.label || l.url}
                </a>
              ))}
            </div>
          )}

          {/* Status + assignee controls */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select
                value={task.status}
                onValueChange={(v) => patch({ status: v as TaskStatus })}
                disabled={savingField}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TASK_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Assignee</Label>
              <Select
                value={assigneeValue}
                onValueChange={(v) => patch({ assignee_id: v === UNASSIGNED ? null : v })}
                disabled={savingField}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                  {interns
                    .filter((i) => i.auth_user_id)
                    .map((i) => (
                      <SelectItem key={i.id} value={i.auth_user_id as string}>
                        {i.display_name || i.email}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Notes / feedback thread */}
          <div className="space-y-3">
            <Label>Notes &amp; feedback</Label>
            {loadingNotes ? (
              <div className="flex justify-center py-4">
                <Loader2 className="size-4 animate-spin text-muted-foreground" />
              </div>
            ) : notes.length === 0 ? (
              <p className="text-sm text-muted-foreground">No notes yet.</p>
            ) : (
              <div className="space-y-2.5">
                {notes.map((n) => (
                  <div
                    key={n.id}
                    className={`rounded-md border p-3 ${n.author === 'ken' ? 'bg-primary/5 border-primary/20' : 'bg-muted/40'}`}
                  >
                    <div className="mb-1 flex items-center gap-2">
                      <span className="text-xs font-semibold">{authorLabel[n.author]}</span>
                      {n.is_blocker && (
                        <Badge variant={n.resolved ? 'secondary' : 'destructive'} className="text-[10px]">
                          {n.resolved ? 'blocker resolved' : 'blocker'}
                        </Badge>
                      )}
                      <span className="ml-auto text-xs text-muted-foreground">
                        {new Date(n.created_at).toLocaleString('en-US', {
                          month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                        })}
                      </span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{n.body}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Admin reply box */}
            <div className="space-y-2">
              <Textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Leave feedback for the intern…"
                rows={3}
              />
              <div className="flex justify-end">
                <Button size="sm" onClick={postReply} disabled={posting || !reply.trim()}>
                  {posting ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
                  Send feedback
                </Button>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
